import type { RequestMessage } from '../../services/supportRequest.service';

/**
 * Is this message new to the current reader?
 *
 * Deliberately the same rule the server counts by (`unread_count` in
 * `src/services/support_request/service.py`): newer than the read cursor, not
 * your own, and not a system event. If these two drift, the sidebar badge and
 * the in-thread badges disagree about the same thread, which reads as a bug
 * whichever one is right.
 *
 * `baseline` is the cursor from *before* the request was opened:
 *   - `undefined` — not known yet; render nothing rather than guess.
 *   - `null`      — never opened; everything from someone else is unread.
 */
export function isUnread(
  message: RequestMessage,
  baseline: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (baseline === undefined) return false;
  // Events are the thread's own bookkeeping, not something addressed to you.
  if (message.author_type === 'system') return false;
  if (currentUserId && message.author_id === currentUserId) return false;
  if (baseline === null) return true;
  return new Date(message.created_at).getTime() > new Date(baseline).getTime();
}

/** How many replies under each root message are unread, keyed by root id. */
export function unreadReplyCounts(
  messages: RequestMessage[],
  baseline: string | null | undefined,
  currentUserId: string | null | undefined,
): Map<string, number> {
  const counts = new Map<string, number>();
  if (baseline === undefined) return counts;

  for (const m of messages) {
    if (!m.parent_message_id) continue;
    if (!isUnread(m, baseline, currentUserId)) continue;
    counts.set(m.parent_message_id, (counts.get(m.parent_message_id) ?? 0) + 1);
  }
  return counts;
}
